import { EventEmitter, Injectable, isDevMode, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CardAction, CardDefinition, CardModel } from '@barthauer/abc/cards-view';

import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  ExecuteQueryDocumentDef,
  QueryHelper,
  QueryObjectsModel,
  ReadDocumentModel,
  SaveDocumentModel,
} from '@barthauer/abc/core/data';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import { AbcQueriesEditorService } from '@barthauer/abc/queries/opath/queries-editor';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { BceComponent } from '../bce.component';
import { BCEDocumentBase, BCEDocumentType, BCEElement, BCEGroupPacket, BCEOpenMode } from '../models/bce.model';
import { DocumentCardBase, GroupCard } from '../viewModels/bce.viewModel';
import { DataService } from './data.service';
import { ParameterService } from '@barthauer/abc/queries/parameters/values-editor';
import { saveAs } from 'file-saver';
import { DataService as CoreDataService } from '@barthauer/abc/core/data';

@Injectable({
  providedIn: 'root',
})
export class BceService {
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  private windowInstance: AbcWindowComponent;
  private groupPacketParameter: BCEGroupPacket[];

  public cardModel: CardModel;
  public cardModelData: Subject<CardModel> = new Subject<CardModel>();
  @Output() selectDocumentCancelled = new EventEmitter();

  constructor(
    private abcContainer: AbcContainerService,
    private dataService: DataService,
    private abcCore: AbcCore,
    private coreDataService: CoreDataService,
    private queriesEditorService: AbcQueriesEditorService,
    private messageBoxService: MessageBoxService,
    private protocolViewerService: ProtocolViewerService,
    private parameterService: ParameterService,
    private messageBox: MessageBoxService,
    sanitizer: DomSanitizer,
    iconRegistry: MatIconRegistry,
  ) {
    iconRegistry.addSvgIcon('Opathquery', sanitizer.bypassSecurityTrustResourceUrl('assets/iconApplication.svg'));
  }

  public bceOpen(groupPacket?: BCEGroupPacket | BCEGroupPacket[], isMobile?: boolean): void {
    if (!isMobile == true) {
      this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    this.dataService.getGroupsCardData(this.groupPacketParameter).subscribe((groups: GroupCard[]) => {
      this.cardModel = new CardModel(groups);
      this.cardModelData.next(this.cardModel);
      if (this.windowInstance) this.windowInstance.showLoader = false;

      const primaryDocumentCardAction: CardAction = new CardAction('Edit', async (cardDefinition) => {
        await this.editDocument(<BCEDocumentBase>cardDefinition.DataObject);
      });

      this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);
      const secondaryDocumentCardAction: CardAction = new CardAction('Execute', async (cardDefinition) => {
        await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
      });

      this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);

      this.attachExportImportActions(this.cardModel.CardDefinitions);
    });
  }
  public selectDocument(
    groupPacket?: BCEGroupPacket | BCEGroupPacket[],
    isMobile?: boolean,
    xPathFilter?: string,
    folderId?: string,
    allowTemplates?: boolean,
  ): Observable<BCEDocumentBase> {
    if (!isMobile == true) {
      this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    const observable$ = new Observable<BCEDocumentBase>((observer) => {
      this.dataService
        .getGroupsCardData(this.groupPacketParameter, folderId, xPathFilter, allowTemplates)
        .subscribe((groups: GroupCard[]) => {
          this.cardModel = new CardModel(groups);
          this.cardModelData.next(this.cardModel);
          if (this.windowInstance) this.windowInstance.showLoader = false;

          const primaryDocumentCardAction: CardAction = new CardAction('Select', (cardDefinition) => {
            observer.next(<BCEDocumentBase>cardDefinition.DataObject);
            if (this.windowInstance) this.windowInstance.closeDialog();
          });
          this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);

          const secondaryDocumentCardAction: CardAction = new CardAction('Execute', async (cardDefinition) => {
            await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
          });
          this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);

          this.attachExportImportActions(this.cardModel.CardDefinitions);
        });
    });
    return observable$;
  }

  private setPrimaryActionOnDocuments(viewModel: CardDefinition[], primaryDocumentCardAction: CardAction): void {
    const mode = primaryDocumentCardAction.DisplayText;
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        element.PrimaryAction = primaryDocumentCardAction;
      } else {
        if (element.CardDefinitions) {
          this.setPrimaryActionOnDocuments(element.CardDefinitions, primaryDocumentCardAction);
        }
      }
    });
  }

  private setSecondaryActionOnDocuments(viewModel: CardDefinition[], secondaryDocumentCardAction: CardAction): void {
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        //SecondaryAction is currently not needed for normal users
        if (isDevMode()) {
          element.SecondaryAction = secondaryDocumentCardAction;
        }
      } else {
        if (element.CardDefinitions) {
          this.setSecondaryActionOnDocuments(element.CardDefinitions, secondaryDocumentCardAction);
        }
      }
    });
  }

  private setExportActionOnDocuments(viewModel: CardDefinition[], exportDocumentCardAction: CardAction): void {
    this.applyDocumentAction(viewModel, (doc) => (doc.ThirdAction = exportDocumentCardAction));
  }

  private setImportActionOnDocuments(viewModel: CardDefinition[], importDocumentCardAction: CardAction): void {
    this.applyDocumentAction(viewModel, (doc) => {
      const dataObject = doc.DataObject as BCEDocumentBase | undefined;
      const isBceDoc = (dataObject as { IsUserDocument?: boolean })?.IsUserDocument === true;
      doc.FourthAction = isBceDoc ? importDocumentCardAction : undefined;
    });
  }

  private applyDocumentAction<T extends BCEElement>(
    viewModel: CardDefinition[],
    assign: (doc: DocumentCardBase<T>) => void,
  ): void {
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        assign(element);
      } else if (element.CardDefinitions) {
        this.applyDocumentAction(element.CardDefinitions, assign);
      }
    });
  }

  private attachExportImportActions(viewModel: CardDefinition[]): void {
    const exportAction = new CardAction('Export', async (cardDefinition) => {
      await this.exportDocument(<BCEDocumentBase>cardDefinition.DataObject);
    });
    const importAction = new CardAction('Import', async (cardDefinition) => {
      await this.importDocument(<BCEDocumentBase>cardDefinition.DataObject);
    });
    this.setExportActionOnDocuments(viewModel, exportAction);
    this.setImportActionOnDocuments(viewModel, importAction);
  }

  public callCloseDialog(): void {
    if (this.windowInstance) this.windowInstance.closeDialog();
  }

  private openAbcWindow(): void {
    const style: WindowStyle = {
      height: '60%',
      minWidth: '400px',
      width: '600px',
      resizable: true,
      shadingColor: '#ffffff80',
      shading: true,
    };
    const WindowAttr: IWindow = {
      id: 'BCEwindow',
      label: 'Barthauer Configuration Explorer',
      maximizeButton: true,
      minimizeButton: true,
      showLoader: true,
      minumizeDialogOption: -64,
      component: BceComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    this.createWindowInstance(WindowAttr);
  }
  private createWindowInstance(WindowAttr: IWindow): void {
    let windowComponentInstance = null;
    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      windowComponentInstance = activeComponent;
      activeComponent.openWindow();
    });

    windowComponentInstance.onCloseClick.subscribe(() => {
      this.selectDocumentCancelled.emit();
      this.windowInstance.showLoader = false;
    });
  }

  private async editDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Edit);
  }

  public async executeDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Execute);
  }

  public async importDocument(doc: BCEDocumentBase): Promise<void> {
    if (!this.ensureDocumentSelected(doc)) return;
    const base64 = await this.pickXmlAsBase64();
    if (!base64) return;
    const save = await this.saveDocumentBase64(doc.Id, base64);
    if (save) {
      this.showInfo('Import erfolgreich.', 'Import');
    } else {
      this.showInfo('Import fehlgeschlagen.', 'Import', true);
    }
  }

  public async exportDocument(doc: BCEDocumentBase): Promise<void> {
    if (!this.ensureDocumentSelected(doc)) return;
    const selection = await this.promptExportFormatSelection();
    const baseName = this.sanitizeFileName(doc?.Designation || 'document');

    if (selection === ExportDocumentType.bce) {
      const document = await this.fetchExportBuffer(doc.Id, 'Bce');
      await this.downloadBuffer(document, `${baseName}.bce`);
    } else if (selection === ExportDocumentType.xml) {
      const document = await this.fetchExportBuffer(doc.Id, 'Xml');
      await this.downloadBuffer(document, `${baseName}.xml`);
    } else {
      this.showInfo('Keine Auswahl getroffen.');
      return;
    }
  }

  private async fetchExportBuffer(documentId: string, exportFormat: 'Xml' | 'Bce'): Promise<Blob | ArrayBuffer> {
    try {
      const model: ReadDocumentModel = new ReadDocumentModel();
      model.Id = documentId;
      model.Indent = true;
      model.ExportFormat = exportFormat;
      if (exportFormat === 'Xml') {
        return await firstValueFrom(this.coreDataService.executeReadDocumentQuery(model));
      } else {
        return await firstValueFrom(this.coreDataService.executeReadDocumentQueryBlob(model));
      }
    } catch {
      return undefined;
    }
  }

  private ensureDocumentSelected(doc: BCEDocumentBase | null | undefined): boolean {
    if (!doc) {
      this.showInfo('Es ist kein Dokument ausgewählt.');
      return false;
    }
    return true;
  }

  private showInfo(message: string, caption?: string, allowCancel?: boolean): void {
    const settings = new MessageSettings();
    settings.caption = caption || '';
    settings.mode = MessageMode.Info;
    settings.button = allowCancel ? MessageBoxButtons.OKCancel : MessageBoxButtons.OK;
    settings.message = message;
    this.messageBox.showMessageBox(settings);
  }

  private async downloadBuffer(data: Blob | ArrayBuffer, fileName: string): Promise<void> {
    if (typeof data !== 'object') {
      data = await new Blob([data], { type: 'application/xml' });
    }
    await this.readBlobText(<Blob>data, fileName);
    this.saveBlobAsFile(<Blob>data, fileName);
  }

  private buildExportFormatSelector(xmlAvailable: boolean, bceAvailable: boolean) {
    const container = this.createEl<HTMLDivElement>('div', 'bce-export-cbx-root');
    const combo = this.createEl<HTMLDivElement>('div', 'combo');
    const label = this.createEl<HTMLSpanElement>('span', 'combo-label', 'Dateiformat');
    const input = this.createEl<HTMLInputElement>('input', 'combo-input');
    input.type = 'text';
    const arrow = this.createEl<HTMLDivElement>('div', 'combo-arrow', '▾');
    const list = this.createEl<HTMLDivElement>('div', 'combo-list');

    const xmlItem = this.createComboItem(list, combo, container, input, 'xml', 'XML', xmlAvailable);
    const bceItem = this.createComboItem(list, combo, container, input, 'bce', 'BCE', bceAvailable);
    list.append(xmlItem, bceItem);

    this.applyDefaultSelection(container, combo, input, bceItem, 'bce', bceAvailable, 'BCE');
    this.attachComboEvents(container, combo, input, arrow, list);

    combo.append(label, input, arrow, list);
    container.appendChild(combo);
    return { container };
  }

  private createEl<T extends HTMLElement>(tag: string, cls?: string, text?: string): T {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text !== undefined) e.textContent = text;
    return e as T;
  }

  private createComboItem(
    list: HTMLDivElement,
    combo: HTMLDivElement,
    container: HTMLDivElement,
    input: HTMLInputElement,
    value: string,
    text: string,
    available: boolean,
  ): HTMLDivElement {
    const item = this.createEl<HTMLDivElement>('div', 'combo-item', text);
    item.dataset.value = value;
    item.style.display = available ? 'block' : 'none';
    item.addEventListener('click', () => {
      input.value = text;
      container.dataset.selected = value;
      combo.classList.add('has-value');
      Array.from(list.children).forEach((c) => c.classList.remove('active'));
      item.classList.add('active');
      list.style.display = 'none';
      combo.classList.remove('open');
    });
    return item;
  }

  private applyDefaultSelection(
    container: HTMLDivElement,
    combo: HTMLDivElement,
    input: HTMLInputElement,
    item: HTMLDivElement,
    value: string,
    available: boolean,
    text: string,
  ): void {
    if (available) {
      input.value = text;
      container.dataset.selected = value;
      combo.classList.add('has-value');
      item.classList.add('active');
    }
  }

  private attachComboEvents(
    container: HTMLDivElement,
    combo: HTMLDivElement,
    input: HTMLInputElement,
    arrow: HTMLDivElement,
    list: HTMLDivElement,
  ): void {
    const positionList = () => {
      const rect = combo.getBoundingClientRect();
      list.style.left = `${rect.left}px`;
      list.style.top = `${rect.bottom}px`;
      list.style.width = `${rect.width}px`;
    };
    const toggleList = () => {
      if (list.style.display === 'none') {
        positionList();
        list.style.display = 'block';
        combo.classList.add('open');
      } else {
        list.style.display = 'none';
        combo.classList.remove('open');
      }
    };
    arrow.addEventListener('click', toggleList);
    input.addEventListener('focus', () => combo.classList.add('open'));
    input.addEventListener('blur', () => {
      if (!container.dataset.selected) combo.classList.remove('open');
    });
    input.addEventListener('input', () => {
      const q = input.value.toLowerCase();
      Array.from(list.children).forEach((c) => {
        const n = c as HTMLElement;
        n.style.display = n.textContent.toLowerCase().includes(q) ? 'block' : 'none';
      });
      positionList();
      list.style.display = 'block';
      combo.classList.add('open');
    });
    window.addEventListener('resize', () => {
      if (list.style.display === 'block') positionList();
    });
    document.addEventListener('click', (ev) => {
      if (!combo.contains(ev.target as Node)) {
        list.style.display = 'none';
        if (!container.dataset.selected) combo.classList.remove('open');
      }
    });
  }

  private async promptExportFormatSelection(): Promise<ExportDocumentType> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Question;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = 'Möchten Sie diese Datei herunterladen?';
    const ui = this.buildExportFormatSelector(true, true);
    settings.htmlcode = ui.container;
    const result = await firstValueFrom(this.messageBox.showMessageBox(settings).pipe(take(1)));
    if (DialogResult.Yes === result['result']) {
      const selected = ui.container?.dataset?.selected || '';
      return selected as ExportDocumentType;
    }
    return null;
  }

  private sanitizeFileName(name: string): string {
    return (name || 'document').replace(/[^a-z0-9-_]+/gi, '_');
  }

  private readBlobText(blob: Blob, fileName: string): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string) ?? '');
      reader.onerror = () => resolve('');
      reader.readAsText(blob);
    });
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  private pickXmlAsBase64(): Promise<string | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xml,application/xml,text/xml';
      input.onchange = () => {
        const file = input.files && input.files.length ? input.files[0] : null;
        if (!file) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(this.dataUrlToBase64(result));
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      };
      input.click();
    });
  }

  private dataUrlToBase64(dataUrl: string): string | null {
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
      const idx = dataUrl.indexOf('base64,');
      return idx >= 0 ? dataUrl.substring(idx + 7) : null;
    }
    return null;
  }

  private async saveDocumentBase64(id: string, base64: string): Promise<boolean> {
    try {
      const model: SaveDocumentModel = new SaveDocumentModel();
      model.Id = id;
      model.Base64Content = base64;
      await firstValueFrom(this.coreDataService.executeSaveDocumentQuery(model).pipe(take(1)));
      return true;
    } catch {
      return false;
    }
  }

  private async openDocumentCore(doc: BCEDocumentBase, mode: BCEOpenMode): Promise<void> {
    if (doc.documentType === BCEDocumentType.OPathQueryDocument) {
      if (await this.isDmlQueryDocument(doc.Id)) {
        if (mode === BCEOpenMode.Execute) {
          await this.executeDmlQueryDocument(doc.Id, doc.Designation);
        } else {
          throw new Error('Editing DML query documents is not supported!');
        }
      } else {
        await this.openOPathQueryDocument(doc.Id, doc.Designation, mode);
      }
    } else {
      throw new Error('Invalid document type: ' + doc.documentType);
    }
  }

  private async openOPathQueryDocument(documentId: string, title: string, mode: BCEOpenMode): Promise<void> {
    try {
      await this.queriesEditorService.runOpathQueriesEditor(documentId, title, mode === BCEOpenMode.Execute);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async isDmlQueryDocument(documentId: string): Promise<boolean> {
    if (!documentId) {
      return false;
    }
    const ids = await this.dataService.queryFilterDocuments([documentId], "//ObjectQueryDocument[@dmlType!='']");
    return await ids?.includes(documentId);
  }

  public async filterDocuments(documentIds: string[], xpathFilter: string): Promise<string[]> {
    return await this.dataService.queryFilterDocuments(documentIds, xpathFilter);
  }

  public async executeOPathQuery(query: QueryObjectsModel, title: string) {
    try {
      await this.queriesEditorService.executeQuery(query, title);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async executeDmlQueryDocument(
    documentId: string,
    title?: string,
    parameterValues?: ParameterValue[],
  ): Promise<void> {
    const executeQueryDocumentDef: ExecuteQueryDocumentDef = new ExecuteQueryDocumentDef();
    executeQueryDocumentDef.Id = documentId;
    executeQueryDocumentDef.Parameters = parameterValues;

    const results = (await this.abcCore.data.executeQueryDocument(executeQueryDocumentDef).toPromise()) as {
      [key: string]: unknown[];
    };
    await this.showDmlProtocol(results, title);
  }

  public async executeDmlQueryDocumentWithParameters(
    documentId: string,
    title?: string,
    parameterValues?: ParameterValue[],
  ): Promise<void> {
    const readDocumentQuery: ReadDocumentModel = new ReadDocumentModel();
    readDocumentQuery.Id = documentId;
    const query: QueryObjectsModel = ConvertHelper.arrayBufferToObject(
      await this.abcCore.data.executeReadQueryFromDocumentQuery(readDocumentQuery).toPromise(),
    );

    if (!!query) {
      const parameters = this.parameterService.tryFindChangeableParameters(
        query,
        parameterValues?.map((_) => _.Name),
      );
      let customParameterValues: ParameterValue[] = await this.parameterService.fillParametersIfExist(parameters);
      if (parameters?.length && !customParameterValues?.length) {
        //Cancelled
        return;
      }

      if (parameterValues?.length) {
        if (!customParameterValues) {
          customParameterValues = [];
        }
        for (const p of parameterValues) {
          customParameterValues.push(p);
        }
      }
      QueryHelper.ensureQueryParameterValues(query, customParameterValues);
      await this.executeDmlQuery(query, title);
    } else {
      const res = await this.dataService.getResourceSet();
      this.showMessageAsHtml(res.get('Query-NotAvailable', documentId), '');
    }
  }

  public async executeDmlQuery(query: QueryObjectsModel, title?: string): Promise<void> {
    const results = await this.abcCore.data.executeManipulateObjectsQuery(query).toPromise();
    await this.showDmlProtocol(results, title);
  }

  private async showDmlProtocol(results: { [key: string]: unknown[] }, title: string): Promise<void> {
    if (results) {
      await this.protocolViewerService.openProtocolViewer(results, undefined, undefined, title);
    } else {
      const res = await this.dataService.getResourceSet();
      await this.showMessageAsHtml(res.get('Execute-NoData'), title);
    }
  }

  private async showMessageAsHtml(htmlMsg: string, title: string): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.icon = MessageBoxIcon.Info;
    settings.button = MessageBoxButtons.OK;
    settings.caption = title;

    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    await this.messageBoxService.showMessageBox(settings).toPromise();
  }
}

enum ExportDocumentType {
  'bce' = 'bce',
  'xml' = 'xml',
}
